import { Chip } from '@mui/material';
import classNames from 'clsx';
import type React from 'react';

export type VNChipTone = 'neutral' | 'primary' | 'success' | 'warning' | 'error' | 'info';
export type VNChipVariant = 'soft' | 'filled' | 'outlined';
export type VNChipDecoration = 'none' | 'dot' | 'pulse-dot' | React.ReactNode;

type VNChipProps = {
  /** Chip text/content. The only required prop. */
  label: React.ReactNode;
  /** Color coding for the chip. Defaults to `neutral`. */
  tone?: VNChipTone;
  /** Fill style. Defaults to `soft`. */
  variant?: VNChipVariant;
  /** Leading decoration: a static dot, a pulsing dot (Live), or an arbitrary node. Defaults to `none`. */
  decoration?: VNChipDecoration;
  /** When true, renders the X (delete) affordance. */
  dismissable?: boolean;
  /** The single action callback, fired when the X is clicked. Ignored unless `dismissable`. */
  onDismiss?: () => void;
  /** Native tooltip, useful for truncated labels. */
  title?: string;
  className?: string;
};

const renderDecoration = (decoration: VNChipDecoration): React.ReactNode => {
  if (decoration === 'none') {
    return null;
  }

  if (decoration === 'dot' || decoration === 'pulse-dot') {
    return (
      <span
        className={classNames('vn-chip__dot', { 'vn-chip__dot--pulse': decoration === 'pulse-dot' })}
        aria-hidden="true"
      />
    );
  }

  return decoration;
};

const VNChip = ({
  label,
  tone = 'neutral',
  variant = 'soft',
  decoration = 'none',
  dismissable = false,
  onDismiss,
  title,
  className,
}: VNChipProps) => {
  const decorationNode = renderDecoration(decoration);

  return (
    <Chip
      size="small"
      title={title}
      className={classNames('vn-chip', `vn-chip--${tone}`, `vn-chip--${variant}`, className)}
      label={
        <span className="vn-chip__content">
          {decorationNode != null && <span className="vn-chip__decoration">{decorationNode}</span>}
          <span className="vn-chip__label">{label}</span>
        </span>
      }
      onDelete={dismissable ? () => onDismiss?.() : undefined}
    />
  );
};

export default VNChip;
